import React from 'react';

/**
 * Procedural mirror schematic.
 *
 * Data model: emitted by shared-lib/vanity-assembly/buildProceduralMirror.js
 * with `type: 'mirror'`, `category: { mirror: true }`,
 * `isProcedural: true`, `modelId: null`. The builder does NOT
 * currently set a `frameStyle` field — the plan listed it as
 * "if present", and it is not. So we render a single visual variant
 * (outer frame + ~1" inner reflection inset), and dispatch on an
 * optional `obj.frameStyle` for forward compatibility.
 *
 * GLB-backed mirrors (modelId set + edgeProjections present) do NOT
 * reach this schematic — they render via the projection path. This
 * registration is only hit on the modelId:null procedural path.
 */

const INCHES_TO_METERS = 0.0254;
const REFLECTION_INSET_M = 1 * INCHES_TO_METERS; // 1" inset per plan

/**
 * Frame style resolution. Defaults to 'frameless' since the builder
 * never sets the field today. 'framed' draws a slightly thicker
 * visual frame (an extra inset stroke) for the future case when a
 * styling pass adds the field.
 */
export function resolveMirrorFrameStyle(obj = {}) {
  if (obj.frameStyle === 'framed' || obj.frameStyle === 'framed-wood') {
    return 'framed';
  }
  return 'frameless';
}

export function MirrorSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  obj = {},
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <MirrorSideView
        depth={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  const style = resolveMirrorFrameStyle(obj);
  // Clamp inset so very small mirrors still show an inner rect.
  const insetX = Math.min(REFLECTION_INSET_M, width * 0.15);
  const insetY = Math.min(REFLECTION_INSET_M, height * 0.15);
  const innerW = Math.max(0, width - insetX * 2);
  const innerH = Math.max(0, height - insetY * 2);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {style === 'framed' && innerW > 0 && innerH > 0 && (
        <rect
          x={insetX * 0.4}
          y={insetY * 0.4}
          width={width - insetX * 0.8}
          height={height - insetY * 0.8}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeOpacity={0.7}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {innerW > 0 && innerH > 0 && (
        <rect
          x={insetX}
          y={insetY}
          width={innerW}
          height={innerH}
          fill={strokeColor}
          fillOpacity={0.06}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeOpacity={0.5}
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
}

/**
 * Side-elevation silhouette for a procedural mirror.
 *
 * Mirrors are essentially flat (~1" deep), so the side view is just a
 * thin tall rectangle the full height of the mirror. Symmetric, so
 * LEFT and RIGHT render identically (the lone rectangle is its own
 * mirror).
 */
function MirrorSideView({ depth, height, strokeColor, strokeWidth }) {
  return (
    <svg
      viewBox={`0 0 ${depth} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      <rect
        x={0}
        y={0}
        width={depth}
        height={height}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

MirrorSchematic.supportsSideViews = true;
